import { createElement, type ComponentType, type ReactElement, type ReactNode } from 'react';
import ApplicationLayout from '@/layouts/application';
import AuthLayout from '@/layouts/auth';
import ConsoleLayout from '@/layouts/console';
import FullscreenLayout from '@/layouts/fullscreen';
import MarketingLayout from '@/layouts/marketing';
import PublicLayout from '@/layouts/public';
import WorkspaceLayout from '@/layouts/workspace';

interface LayoutPageProps {
  reactLayout?: string;
  [key: string]: unknown;
}

export interface InertiaPageModule {
  default: {
    layout?: (page: ReactElement) => ReactNode;
  };
}

/**
 * Resolves the layout for a page based on controller-specified reactLayout prop
 * or falls back to the default ApplicationLayout.
 *
 * Controllers can specify layout via: `react_layout "workspace"`
 *
 * Available layouts:
 * - "workspace" - Authenticated Linear workspace
 * - "auth" - Login / session screens (light)
 * - "fullscreen" - No chrome
 * - "marketing" - Public homepage chrome
 * - "console" - The builder console and staff admin
 * - "public" - Pay page and published app front door
 * - Default - Uses ApplicationLayout
 */
export default function resolvePageLayout(_name: string, page: unknown): InertiaPageModule {
  const module = page as InertiaPageModule;

  if (module.default.layout) {
    return module;
  }

  module.default.layout = (pageComponent: ReactElement) => {
    const pageProps = (pageComponent.props || {}) as LayoutPageProps;
    const Layout = layoutComponent(pageProps.reactLayout);
    return createElement(Layout, pageProps, pageComponent);
  };

  return module;
}

function layoutComponent(name: string | undefined): ComponentType<LayoutPageProps> {
  if (name === 'fullscreen') return FullscreenLayout as unknown as ComponentType<LayoutPageProps>;
  if (name === 'auth') return AuthLayout as unknown as ComponentType<LayoutPageProps>;
  if (name === 'workspace') return WorkspaceLayout as unknown as ComponentType<LayoutPageProps>;
  if (name === 'marketing') return MarketingLayout as unknown as ComponentType<LayoutPageProps>;
  if (name === 'console') return ConsoleLayout as unknown as ComponentType<LayoutPageProps>;
  if (name === 'public') return PublicLayout as unknown as ComponentType<LayoutPageProps>;
  return ApplicationLayout as unknown as ComponentType<LayoutPageProps>;
}
